import { useCommonCriteria } from '@src/entities/Criteria/ui/hooks/useCommonCriteria';
import { useCommonCriteriaVersions } from '@src/entities/Criteria/ui/hooks/useCommonCriteriaVersions';
import { useWizardContext } from '@src/features/ActionPaths/Wizard/context';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { AdditionalCriteriaWidget, CommonCriteriaWidget } from '@src/widgets/Criteria';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { isEmpty, isUndefined } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { ExitCriteriaMethodField } from '../../fields/ExitCriteriaMethod';

/**
 * ExitCriteria
 *
 * Provides the user with a screen to specify a list of filters to find risk records by.
 *
 * @param form
 * @param setSelectedCommonExit
 * @returns {Element}
 * @constructor
 */
export function ExitCriteria({ form = {}, setSelectedCommonExit = () => {} }) {
  const exitCriteriaMethod = form.getValues('exitCriteriaMethod');

  const { action, actionPath, currentStep } = useWizardContext();

  const [initialFetch, setInitialFetch] = useState(false);
  const { criteriaList, fetch: fetchCriteria } = useCommonCriteria();
  const { fetch: fetchCriteriaVersions, versionsList } = useCommonCriteriaVersions();

  let isActiveAndManual = false;

  if (
    typeof actionPath?.status === 'string' &&
    actionPath.status.includes('ACTIVE') &&
    actionPath?.manualAssociation === true
  ) {
    isActiveAndManual = true;
  }

  /**
   * retrieve common criteria and common criteria versions saved to the action path
   */
  useEffect(() => {
    if (initialFetch === false && !isUndefined(actionPath?.criteria) && !isEmpty(actionPath?.criteria)) {
      fetchCriteria(actionPath?.criteria);
      fetchCriteriaVersions(actionPath?.criteria);
      setInitialFetch(true);
    }
  }, [actionPath, initialFetch]);

  /**
   * loop through criteriaList and versionsList to determine if the versions are a mismatch
   * @type {{}}
   */
  const needsUpdate = useMemo(() => {
    const items = {};

    if (!isEmpty(criteriaList) && !isEmpty(versionsList)) {
      criteriaList
        ?.filter((item) => {
          return item?.isActive;
        })
        .forEach((criteria) => {
          const version = versionsList.find((item) => {
            return criteria?.id === item?.commonCriteriaId;
          });

          if (!isUndefined(version) && criteria?.activeVersionNbr !== version?.version) {
            items[criteria?.id] = {
              from: version,
              to: criteria?.activeCommonCriteriaVersion,
            };
          }
        });
    }

    return items;
  }, [criteriaList, versionsList, actionPath]);

  /**
   * loop through criteriaList and versionsList to determine if the versions have been deactivated
   * @type {{}}
   */
  const needsRemoval = useMemo(() => {
    const items = {};

    if (!isEmpty(criteriaList) && !isEmpty(versionsList)) {
      criteriaList.forEach((criteria) => {
        const version = versionsList.find((item) => {
          return criteria?.id === item?.commonCriteriaId;
        });

        if (!isUndefined(version) && criteria?.isActive === false) {
          items[criteria?.id] = {
            criteria,
            version,
          };
        }
      });
    }

    return items;
  }, [criteriaList, versionsList, actionPath]);

  /**
   * loop through criteriaList and versionsList to determine if action needs to be taken
   * @type {{update: *[], remove: *[]}}
   */
  const needsReview = useMemo(() => {
    const items = {
      remove: [],
      update: [],
    };

    if (!isEmpty(criteriaList) && !isEmpty(versionsList)) {
      criteriaList
        .filter((item) => {
          return item?.isActive;
        })
        .forEach((criteria) => {
          const version = versionsList.find((item) => {
            return criteria?.id === item?.commonCriteriaId;
          });

          if (!isUndefined(version) && criteria?.activeVersionNbr !== version?.version) {
            items.update.push(criteria?.name);
          }
        });

      criteriaList.forEach((criteria) => {
        const version = versionsList.find((item) => {
          return criteria?.id === item?.commonCriteriaId;
        });

        if (!isUndefined(version) && criteria?.isActive === false) {
          items.remove.push(criteria?.name);
        }
      });
    }

    return items;
  }, [criteriaList, versionsList, actionPath]);

  /**
   * check if needsUpdate or needsRemoval is not empty and set error message
   */
  useEffect(() => {
    if (!isEmpty(needsUpdate) || !isEmpty(needsRemoval)) {
      if (isUndefined(form?.formState?.errors?.criteria?.exit?.commonCriteriaVersionsIds)) {
        form?.setError('criteria.exit.commonCriteriaVersionsIds', {
          message: 'Common criteria needs review.',
          type: 'invalidCriteria',
        });
      }
    }

    if (
      isEmpty(needsUpdate) &&
      isEmpty(needsRemoval) &&
      !isUndefined(form?.formState?.errors?.criteria?.exit?.commonCriteriaVersionsIds)
    ) {
      form?.clearErrors('criteria.exit.commonCriteriaVersionsIds');
    }
  }, [needsUpdate, needsRemoval]);

  return (
    <ErrorHandler location="src/features/ActionPaths/Wizard/components/steps/ExitCriteria/ExitCriteria.jsx">
      <Grid>
        <Grid.Col css={{ paddingTop: 'var(--abyss-space-lg)' }} span={{ xs: '100%' }}>
          <Layout.Stack grow space={0}>
            <Heading offset={1}>{currentStep?.label}</Heading>
            <p>{currentStep?.description}</p>
          </Layout.Stack>
        </Grid.Col>

        <Grid.Col span={{ xs: '100%' }}>
          <Layout.Group>
            <ExitCriteriaMethodField isActiveAndManual={isActiveAndManual} />
          </Layout.Group>
        </Grid.Col>

        {exitCriteriaMethod === 'EXIT_CRITERIA_MET' && [
          <Grid.Col key="commonCriteriaWidget" span={{ xs: '100%' }}>
            <CommonCriteriaWidget
              context="exit"
              fetchCriteria={fetchCriteria}
              fetchCriteriaVersions={fetchCriteriaVersions}
              filterKey="criteria.exit.common"
              form={form}
              needsRemoval={needsRemoval}
              needsReview={needsReview}
              needsUpdate={needsUpdate}
              setSelected={setSelectedCommonExit}
              showActions={action === 'edit'}
            />
          </Grid.Col>,
          <Grid.Col key="additionalCriteriaWidget" span={{ xs: '100%' }}>
            <AdditionalCriteriaWidget filterKey="criteria.exit.additional" form={form} />
          </Grid.Col>,
        ]}
      </Grid>
    </ErrorHandler>
  );
}
